import { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { useMeterkastUploadMutation } from '../../api/queries/meterkast';
import Alert from '../../components/Alert/Alert';
import BackButton from '../../components/BackButton';
import Button from '../../components/Button';
import CheckBoxInput from '../../components/CheckBoxInput/CheckBoxInput';
import MaterialIcon from '../../components/MaterialIcon/MaterialIcon';
import { StepsContext } from '../../components/Steps/StepsContext';
import { TicketContext } from '../../contexts/TicketContext';
import { useScrollToTop } from '../../helpers/hooks/useScrollToTop';
import { TicketRoutePaths } from '../../interfaces/Routes';
import usePageTitle from '../../services/hooks/usePageTitle';
import StepsService from '../../services/StepsService';
import PhotoExamplesModal from './components/PhotoExamplesModal/PhotoExamplesModal';
import PhotoPicker from './components/PhotoPicker/PhotoPicker';
import styles from './Meterkast.module.scss';

const Meterkast: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { steps } = useContext(StepsContext);
  const hasPreviousPage = !!state?.hasPreviousPage;

  const { ticket, fetchTicket } = useContext(TicketContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { uploadMeterkastPhotos } = useMeterkastUploadMutation();

  const [photos, setPhotos] = useState<File[]>([]);
  const [consent, setConsent] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isExamplesOpen, setIsExamplesOpen] = useState(false);

  usePageTitle(t('pageTitle:meterkast'));
  useScrollToTop();

  useEffect(() => {
    if (ticket?.reportStatus === 'disabled') {
      navigate(StepsService.getActiveStep(steps).uri, { replace: true });
    } else if (ticket?.reportStatus === 'processing') {
      // for customer would visit page again after submitting and closing
      navigate(StepsService.getStepUri(ticket.hashId, TicketRoutePaths.meterkastScheduled), { replace: true });
    }
  }, [navigate, steps, ticket?.reportStatus, ticket?.hashId]);

  if (!ticket) return null;

  const hasEmail = !!ticket.customer?.email;
  const canSubmit = photos.length > 0 && consent && privacyConsent && hasEmail && !isProcessing && !isSubmitting;

  const onSubmit = async () => {
    setHasError(false);
    setIsSubmitting(true);

    try {
      await uploadMeterkastPhotos(photos);
      await fetchTicket(ticket.hashId);
      navigate(StepsService.getStepUri(ticket.hashId, TicketRoutePaths.meterkastScheduled), { replace: true });
    } catch {
      setHasError(true);
      setIsSubmitting(false); // if successful, loading doesnt matter
    }
  };

  return (
    <div className={styles.page}>
      {hasPreviousPage && <BackButton ariaLabel={t('button:back')} text={t('button:back')} useRouter />}

      <h1 className={styles.title}>{t('meterkast:captureTitle')}</h1>
      <p className={styles.body}>{t('meterkast:captureBody')}</p>
      <button
        className={styles.textLink}
        onClick={() => setIsExamplesOpen(true)}
        type="button"
      >
        <span>{t('meterkast:photoExamplesLink')}</span>
      </button>

      {isExamplesOpen && <PhotoExamplesModal onClose={() => setIsExamplesOpen(false)} />}

      <PhotoPicker
        onChange={setPhotos}
        onProcessingChange={setIsProcessing}
        photos={photos}
      />

      <div className={styles.consentBlock}>
        <h2 className={styles.consentTitle}>{t('meterkast:consentTitle')}</h2>
        <p className={styles.consentBody}>{t('meterkast:consentBody')}</p>
        <CheckBoxInput
          id="meterkast-consent"
          isChecked={consent}
          label={t('meterkast:consentCheckbox')}
          onCheck={setConsent}
          smallLabel
        />
        <CheckBoxInput
          id="meterkast-privacyconsent"
          isChecked={privacyConsent}
          label={t('meterkast:privacyConsentCheckbox')}
          onCheck={setPrivacyConsent}
          smallLabel
        />
        <a
          className={styles.textLink}
          href={StepsService.getStepUri(ticket.hashId, TicketRoutePaths.meterkastPrivacyPolicy)}
          rel="noopener noreferrer"
          target="_blank"
        >
          <span>{t('meterkast:privacyPolicyLink')}</span>
          <MaterialIcon name="open_in_new" />
        </a>
      </div>

      {!hasEmail && <Alert level="error" text={t('meterkast:noEmailError')} />}
      {hasError && <Alert level="error" text={t('meterkast:uploadError')} />}

      <div className={styles.actionRow}>
        <Button
          disabled={!canSubmit}
          isLoading={isSubmitting}
          onClick={onSubmit}
          size="full"
          text={t('meterkast:submitButton')}
        />
      </div>
    </div>
  );
};

export default Meterkast;
